/**
 * RAG LAB — CodeAI101 chapter 5, search + paste + ask, with the search really happening.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 5.
 *
 * ── IT REFUSES TO ANSWER, AND THAT IS THE HONEST HALF ─────────────────────────────────────────
 * There is no model here. A lab that printed a plausible answer would be doing exactly what §1
 * warns about — a fluent sentence with nothing behind it — inside the chapter that teaches
 * grounding. So it does steps one and two for real and stops at three, showing the assembled
 * prompt instead of a reply. By the module's own argument that is not a gap: "The hard part is not
 * the AI; it is the retrieval… Bad retrieval means the model gets the wrong chunks and produces a
 * confident wrong answer." What is on screen is the thing that decides the answer.
 *
 * ── THE CORPUS IS THIS COURSE, AND THE SEARCH IS ARITHMETIC ───────────────────────────────────
 * Sixty-three real chunks, already compiled for the reading surface. Every score shown, every
 * matched word listed. Nothing invented to search over and nothing hidden about how it ranked.
 *
 * ── AND THE COMPARISON IS MEASURED, NOT STAGED ────────────────────────────────────────────────
 * Two retrievers, and the naive one is not crippled. On this corpus the two mostly agree — which is
 * the truth about small single-subject collections, and a more useful thing to learn than a rigged
 * contrast. The lab reports what they actually did on the learner's question and names why.
 */

import { useMemo, useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import Anchor from './anchor.jsx';
import SHELF from '../../../codeai101/codeai101-shelf.json';
import {
  buildIndex, retrieve, compare, stuff, RETRIEVERS, STEPS, PROBLEMS, OPTIONS,
  TEMPLATE, RULE_OF_THUMB, NOT_EXOTIC, HARD_PART, THE_FIX, WHY_NO_ANSWER, WHY_SO_CLOSE,
  SOURCE_1, SOURCE_2, SOURCE_7,
} from './retrieval.mjs';

const EXAMPLES = [
  'what is a context window',
  'how do I stop an agent looping forever',
  'is my company data safe',
  'why is it wrong sometimes',
];

export default function RagLab() {
  const index = useMemo(() => buildIndex(SHELF.entries), []);
  const [q, setQ] = useState('');
  const [how, setHow] = useState('distinctive');
  const [asked, setAsked] = useState(null);

  const ask = (text) => { const t = text.trim(); if (!t) return; setQ(t); setAsked(t); };
  const r = asked ? retrieve(index, asked, { how, k: 3 }) : null;
  const cmp = asked ? compare(index, asked, 3) : null;

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">RAG lab · search + paste + ask</div>
      <h2 className="g-h2 lab-h">The model is the same. The chunks are not.</h2>
      <Anchor chapter={5} />
      <p className="g-body"><Value of={cited(NOT_EXOTIC, SOURCE_2)} /></p>

      {/* ── ASK ── */}
      <div className="lab-task-head">
        <input className="morse-input rag-q" value={q} placeholder="ask this course something…"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') ask(q); }} />
        <button className="g-btn g-btn-primary" disabled={!q.trim()} onClick={() => ask(q)}>Retrieve</button>
      </div>
      <div className="lab-task-head">
        <span className="g-label">or try</span>
        {EXAMPLES.map((e) => (
          <button key={e} className="g-btn rag-eg" onClick={() => ask(e)}>{e}</button>
        ))}
      </div>
      <div className="lab-task-head">
        <span className="g-label">Retriever</span>
        {Object.values(RETRIEVERS).map((x) => (
          <button key={x.id} className={`g-btn ${how === x.id ? 'g-btn-primary' : ''}`}
            onClick={() => setHow(x.id)}>{x.name}</button>
        ))}
      </div>
      <p className="g-body g-dim rag-why">{RETRIEVERS[how].why}</p>

      {asked && (
        <>
          {/* ── 1 · RETRIEVE ── */}
          <div className="lab-task">
            <div className="g-label">1 · {STEPS[0].name} — searched {r.considered} chunks of this course</div>
            {!r.anyFound && (
              <p className="g-body">Nothing matched. The collection has no chunk containing any of
                those words, so a real system would have nothing to stuff — and an ungrounded model
                would answer anyway.</p>
            )}
            {r.hits.map((h, i) => (
              <div key={h.title} className="rag-hit">
                <div className="rag-hhead">
                  <span className="cou-n">{i + 1}</span>
                  <b>{h.title.replace(/^Module (\d+) · [^—]*— /, 'Module $1 · ')}</b>
                  <span className="cou-sp" />
                  <span className="cou-ms">{h.score}</span>
                </div>
                <p className="g-body rag-body">{h.body.slice(0, 260)}…</p>
                <p className="g-body g-dim rag-matched">matched on: {h.matched.join(', ') || '—'}</p>
              </div>
            ))}
          </div>

          {/* ── 2 · STUFF ── */}
          {r.anyFound && (
            <div className="lab-task">
              <div className="g-label">2 · {STEPS[1].name} — this is the prompt that would be sent</div>
              <p className="g-body g-dim"><Value of={cited(TEMPLATE, SOURCE_2)} /></p>
              <pre className="rag-prompt">{stuff(r.hits, asked)}</pre>
            </div>
          )}

          {/* ── 3 · ANSWER, REFUSED ── */}
          <div className="lab-task">
            <div className="g-label">3 · {STEPS[2].name}</div>
            <Value of={refused(WHY_NO_ANSWER)} />
            <p className="g-body"><Value of={cited(HARD_PART, SOURCE_2)} /></p>
          </div>

          {/* ── WHAT THE TWO RETRIEVERS ACTUALLY DID ── */}
          <div className="lab-task">
            <div className="g-label">Did the retriever matter, on this question?</div>
            <div className="lab-readout">
              <div><span className="g-label">Same top three</span>
                <Value of={computed(cmp.same ? 'yes' : 'no')} /></div>
              <div><span className="g-label">Same first hit</span>
                <Value of={computed(cmp.sharedTop ? 'yes' : 'no')} /></div>
              {!cmp.same && <div><span className="g-label">They first differ at</span>
                <Value of={computed(`#${cmp.differsFrom}`)} /></div>}
            </div>
            <p className="g-body g-dim">{WHY_SO_CLOSE}</p>
          </div>
        </>
      )}

      {/* ── WHY RAG EXISTS ── */}
      <div className="lab-task">
        <div className="g-label">Why any of this is necessary</div>
        {PROBLEMS.map((p) => (
          <p key={p.id} className="g-body"><b>{p.name}.</b> <Value of={cited(p.quote, SOURCE_1)} /></p>
        ))}
        <p className="g-body rag-punch"><Value of={cited(THE_FIX, SOURCE_1)} /></p>
      </div>

      <details className="cou-tr">
        <summary className="g-label">RAG, a longer prompt, or fine-tuning?</summary>
        <dl className="cou-dl">
          {OPTIONS.map((o) => <div key={o.id}><dt>{o.name}</dt><dd>{o.quote}</dd></div>)}
        </dl>
        <p className="g-body"><b>Rule of thumb:</b> <Value of={cited(RULE_OF_THUMB, SOURCE_7)} /></p>
      </details>

      <p className="g-body g-dim lab-why">
        The retrieval above is real — 63 chunks of this course, scored by arithmetic you can read in
        the source, with every matched word listed. The answer is not shown because there is no
        model here, and printing a plausible one would be manufacturing exactly the fluent
        unsupported sentence this chapter teaches you to distrust.
        <br />
        The two retrievers are both honest implementations; the naive one has not been crippled to
        make a point. Measured across eight questions it picked a different top three on five of
        them, always at the second or third hit — which is what a small, single-subject collection
        looks like, and is a more useful thing to know than a rigged contrast would have been.
      </p>
    </div>
  );
}
